
import { Group, Mesh, Vector3, MeshBasicMaterial, BoxGeometry } from "../../three.module.js";
import { GameSceneService } from "../../services/game-scene.service.js";
import { parseFirstIntersect } from "../../GameTools.js";

export class DevelopingToolingKit { 

    static HELP_MESH_EXPIRATION_MS = 3000;

    /** @type {undefined | DevelopingToolingKit} */
    static instance;

    static getInstance() {
        
        if (!DevelopingToolingKit.instance) {
            DevelopingToolingKit.instance = new DevelopingToolingKit();
        }

        return DevelopingToolingKit.instance;
    }

    enabled     = false;

    group       = new Group();
    help_meshes = new Group();

    default_help_mesh = new Mesh( new BoxGeometry( 0.05, 0.05, 0.05 ), new MeshBasicMaterial({ color: 0 }));

    gameSceneService = GameSceneService.getInstance();

    constructor() {

        if (DevelopingToolingKit.instance) throw "Singleton!"

        this.group.name       = "DevelopingToolingKit";
        this.help_meshes.name = "DevelopingToolingKitHelpMeshes";
        this.group.add( this.help_meshes );

        this.enable();
    }

    /** @type {number | null} */
    loopInterval = null;

    loop() {
        let timestamp = Date.now();

        for (let mesh of this.help_meshes.children) {
            if (mesh.userData.expires && mesh.userData.expires < timestamp) {
                this.help_meshes.remove( mesh );
                this.gameSceneService.renderNeeded = true;
            }
        }
    }

    enable() {
        if (this.enabled) return;

        this.enabled = true;
        this.gameSceneService.scene.add( this.group );

        this.loopInterval = setInterval(() => {
            this.loop();
        }, 1000);
    }

    disable() {
        if (!this.enabled) return;

        if (this.loopInterval) clearInterval( this.loopInterval );
        this.loopInterval = null;

        this.enabled = false;
        this.gameSceneService.scene.remove( this.group );
    }

    dispose() {
        this.disable();
        this.group.dispose();
    }

    /**
     * @param {import("three").Vector3} position
     */
    generateHelpMesh( position, info = {} ) {

        let mesh = this.default_help_mesh.clone();

        if (!position) return console.error( "Missing required argument position" );

        this.addHelpMesh( mesh, position, info );
    }

    /**
     * @param {import("three").Mesh} mesh
     * @param {import("three").Vector3 | undefined} position
     */
    addHelpMesh( mesh, position = undefined, info = {} ) {

        if (position) mesh.position.copy( position );
       
        let timestamp = Date.now();

        mesh.userData.timestamp = timestamp;
        mesh.userData.info = info;

        if (DevelopingToolingKit.HELP_MESH_EXPIRATION_MS > 0) {
            mesh.userData.expires = timestamp + DevelopingToolingKit.HELP_MESH_EXPIRATION_MS;
        }

        this.help_meshes.add( mesh );
        this.gameSceneService.renderNeeded = true;
    }

    static clear() {

        let tooling = DevelopingToolingKit.getInstance();

        if (!tooling?.enabled) return;

        for (let mesh of tooling.help_meshes.children) {
            tooling.help_meshes.remove( mesh );
        }

        tooling.gameSceneService.renderNeeded = true;
    }

    /**
     * @param {import("three").Intersection[]} intersects
     */
    static visualizeIntersects( intersects ) {

        let tooling = DevelopingToolingKit.getInstance();

        if (!tooling?.enabled) return;

        for (let intersect of intersects) {
            if (intersect.object && intersect.point) {
                tooling.generateHelpMesh( intersect.point, { intersect });
            }
        }
    }

    /**
     * @param {import("three").Raycaster} raycaster
     */
    static visualizeRaycaster( raycaster ) {

        let tooling = DevelopingToolingKit.getInstance();

        if (!tooling?.enabled) return;

        if (!raycaster) return console.error("Missing required argument raycaster");
        
        let start_mesh = new Mesh( new BoxGeometry( 0.05, 0.05, 0.05 ), new MeshBasicMaterial({ color: 0x00ff00 }))
        let end_mesh   = new Mesh( new BoxGeometry( 0.05, 0.05, 0.05 ), new MeshBasicMaterial({ color: 0xff0000 }))
        let step_mesh  = new Mesh( new BoxGeometry( 0.01, 0.01, 0.01 ), new MeshBasicMaterial({ color: 0x0000ff }))

        tooling.addHelpMesh( start_mesh, raycaster.ray.origin );

        let distance = 3;

        // Calculate a point along the ray's direction
        const rayEndPoint = new Vector3()
            .copy(raycaster.ray.direction)
            .multiplyScalar( distance ) 
            .add(raycaster.ray.origin);

        // Create a mesh at the end point
        tooling.addHelpMesh( end_mesh, rayEndPoint );

        for (let i = 0.1; i < distance; i += 0.1) {
            const point = raycaster.ray.origin.clone().add( raycaster.ray.direction.clone().multiplyScalar( i ) );
            tooling.addHelpMesh( step_mesh.clone(), point );
        }
    }
}